import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ArrowUpRight,
  Award,
  BookOpen,
  CheckCircle2,
  Mail,
  MessageCircle,
  Phone,
  Play,
  Sparkles,
} from 'lucide-react';
import { useAuth } from '../components/auth/AuthContext';
import AnnouncementsCard from '../components/dashboard/AnnouncementsCard';
import { getMyEnrollments, getMyPayments, getPublishedCourses, getCompletedLessonIds } from '../lib/lms';
import { getMyCertificates } from '../lib/certificates';
import type { StudentDashboardData } from '../types/dashboard';
import { EmptyState, PageHeader, Panel, PanelHead, Stamp } from '../components/lms/ui';
import LearningPath from '../components/lms/LearningPath';
import { DashboardSkeleton } from '../components/lms/Skeletons';

const courseRef = (id: string) => `LSEM-${id.replace(/[^a-zA-Z0-9]/g, '').slice(0, 6).toUpperCase()}`;

const Dashboard = () => {
  const { user } = useAuth();
  const [data, setData] = useState<StudentDashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!user) return;
      setLoading(true);
      try {
        const [enrollments, payments, published, certs] = await Promise.all([
          getMyEnrollments(user.id),
          getMyPayments(user.id),
          getPublishedCourses(),
          getMyCertificates(user.id).catch(() => []),
        ]);
        if (cancelled) return;

        const enrolledCourses = await Promise.all(
          enrollments
            .filter((e) => e.status !== 'revoked')
            .map(async (e) => {
              const lessons = e.course?.modules.flatMap((m) => m.lessons) ?? [];
              const done = await getCompletedLessonIds(
                user.id,
                lessons.map((l) => l.id)
              ).catch(() => new Set<string>());
              const total = lessons.length;
              const completedCount = Math.min(done.size, total);
              const pct = total === 0 ? 0 : Math.min(100, Math.round((completedCount / total) * 100));
              const liveCourse = published.find((course) => course.id === e.course_id) ?? e.course;
              return {
                courseId: e.course_id,
                courseName: e.course?.title ?? e.course_id,
                thumbnail: liveCourse?.thumbnail ?? e.course?.thumbnail ?? '',
                progress: pct,
                totalClasses: lessons.length,
                completedClasses: completedCount,
                nextClass: {
                  date: 'Self-paced',
                  topic: lessons.find((l) => !done.has(l.id))?.title ?? 'All lessons complete',
                  time: 'Anytime',
                },
                grades: { assignments: 0, quizzes: 0, projects: 0, overall: pct },
              };
            })
        );

        const enrolledIds = new Set(enrollments.map((e) => e.course_id));
        const recommended = published
          .filter((c) => !enrolledIds.has(c.id))
          .slice(0, 3)
          .map((c) => ({
            id: c.id,
            title: c.title,
            description: c.tagline || c.description,
            price: c.price_value === 0 ? 'Free' : `₦${c.price_value.toLocaleString('en-NG')}`,
            image: c.thumbnail || '',
          }));

        const paidByCourse = new Map<string, number>();
        payments
          .filter((p) => p.status === 'completed' && p.course_id)
          .forEach((p) => paidByCourse.set(p.course_id!, (paidByCourse.get(p.course_id!) ?? 0) + p.amount));

        const paymentCards = enrolledCourses.map((c) => {
          const course = published.find((x) => x.id === c.courseId) ?? enrollments.find((e) => e.course_id === c.courseId)?.course;
          const total = course?.price_value ?? 0;
          const paid = paidByCourse.get(c.courseId) ?? (enrollments.find((e) => e.course_id === c.courseId)?.paystack_ref ? total : 0);
          const balance = Math.max(total - paid, 0);
          return {
            courseId: c.courseId,
            courseName: c.courseName,
            totalAmount: total,
            amountPaid: paid,
            nextPayment: { amount: balance, dueDate: balance > 0 ? 'Pay balance to unlock certificate' : 'Fully paid' },
            installments: [
              { amount: paid, dueDate: 'Paid to date', status: 'paid' as const },
              ...(balance > 0 ? [{ amount: balance, dueDate: 'Outstanding', status: 'pending' as const }] : []),
            ],
          };
        });

        const completedCount = enrolledCourses.filter((c) => c.progress >= 100).length;
        setData({
          enrolledCourses,
          payments: paymentCards,
          recommendedCourses: recommended,
          achievements: {
            certificates: certs.length,
            completedCourses: completedCount,
            totalHours: enrolledCourses.reduce((s, c) => s + c.completedClasses, 0),
            averageGrade: enrolledCourses.length
              ? Math.round(enrolledCourses.reduce((s, c) => s + c.progress, 0) / enrolledCourses.length)
              : 0,
          },
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [user]);

  const firstName = user?.name?.trim().split(/\s+/)[0] || 'Student';
  const recommendedCourses = data?.recommendedCourses ?? [];

  if (loading) {
    return <DashboardSkeleton />;
  }

  if (!data || data.enrolledCourses.length === 0) {
    return (
      <div>
        <PageHeader
          eyebrow="Student overview"
          title={`Welcome, ${firstName}.`}
          lede="Your learning space is ready. Choose a programme to open your first classroom, track lessons and earn a verifiable certificate."
          actions={
            <Link to="/courses" className="lms-btn-primary">
              Explore programmes <ArrowRight className="h-4 w-4" strokeWidth={1.7} />
            </Link>
          }
        />
        <AnnouncementsCard prominent className="mt-7" />
        <div className="mt-5 grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
          <Panel className="overflow-hidden border-blue-100">
            <div className="relative overflow-hidden bg-gradient-to-br from-blue-700 via-blue-600 to-sky-500 px-6 py-8 text-white sm:px-8">
              <div className="lms-hero-grid absolute inset-0" aria-hidden="true" />
              <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/20">
                  <Sparkles className="h-7 w-7" strokeWidth={1.5} />
                </span>
                <div>
                  <p className="font-mono text-[9.5px] uppercase tracking-[0.15em] text-blue-100">Your first step</p>
                  <p className="mt-1.5 max-w-[36ch] text-[20px] font-semibold leading-snug tracking-[-0.02em]">
                    Pick a pathway and start learning today.
                  </p>
                  <p className="mt-2 max-w-[52ch] text-[12.5px] leading-5 text-blue-100">
                    Free programmes open immediately. Paid programmes unlock as soon as Paystack confirms your payment.
                  </p>
                </div>
              </div>
            </div>
            <EmptyState
              title="No programme on file yet"
              body="The programme register holds every LSEM pathway, from short certificates to the Mini MBA."
              action={<Link to="/courses" className="lms-btn-primary">Browse programmes</Link>}
            />
          </Panel>
          <Panel>
            <PanelHead title="Student desk" />
            <div className="space-y-1 p-3">
              <a href="mailto:info@lsem.org.ng" className="flex items-center gap-3 rounded-xl px-3 py-3 text-[13px] text-ink transition-colors hover:bg-blue-50">
                <Mail className="h-4 w-4 text-blue-600" strokeWidth={1.6} /> info@lsem.org.ng
              </a>
              <a href="tel:+2349058681595" className="flex items-center gap-3 rounded-xl px-3 py-3 text-[13px] text-ink transition-colors hover:bg-blue-50">
                <Phone className="h-4 w-4 text-blue-600" strokeWidth={1.6} /> +234 905 868 1595
              </a>
              <a href="https://wa.me/2349058681595" target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded-xl px-3 py-3 text-[13px] text-ink transition-colors hover:bg-blue-50">
                <MessageCircle className="h-4 w-4 text-blue-600" strokeWidth={1.6} /> WhatsApp support
              </a>
            </div>
          </Panel>
        </div>

        {recommendedCourses.length > 0 && (
          <Panel className="mt-6">
            <PanelHead
              title="New & recommended programmes"
              action={<Link to="/courses" className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-700 hover:text-blue-900">View register <ArrowRight className="h-3 w-3" /></Link>}
            />
            <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-3">
              {recommendedCourses.map((course) => (
                <Link
                  key={course.id}
                  to={`/course/${course.id}`}
                  className="group flex flex-col rounded-2xl border border-rule bg-white p-4 transition-all hover:border-blue-200 hover:shadow-[0_14px_34px_-22px_rgba(29,78,216,0.45)]"
                >
                  <span className="lms-kicker">{course.price}</span>
                  <span className="mt-2 text-[14px] font-semibold leading-snug text-ink group-hover:text-blue-700">{course.title}</span>
                  <span className="mt-1.5 line-clamp-2 text-[11.5px] leading-5 text-ink-2">{course.description}</span>
                  <span className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold text-blue-700">
                    View programme <ArrowUpRight className="h-3 w-3" />
                  </span>
                </Link>
              ))}
            </div>
          </Panel>
        )}
      </div>
    );
  }

  const activeCourse =
    data.enrolledCourses.find((c) => c.progress > 0 && c.progress < 100) ??
    data.enrolledCourses.find((c) => c.progress < 100) ??
    data.enrolledCourses[0];
  const totalLessons = data.enrolledCourses.reduce((s, c) => s + c.totalClasses, 0);
  const completedLessons = data.enrolledCourses.reduce((s, c) => s + c.completedClasses, 0);
  const remaining = Math.max(totalLessons - completedLessons, 0);
  const fees = data.payments.filter((p) => p.totalAmount > 0);
  const today = new Date().toLocaleDateString('en-NG', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <p className="lms-kicker">{today}</p>
          <h1 className="mt-2 text-[clamp(1.85rem,2.6vw,2.5rem)] font-semibold leading-[1.08] tracking-[-0.035em] text-ink">
            Welcome back, {firstName}.
          </h1>
          <p className="mt-2 text-[13.5px] leading-6 text-ink-2">
            {data.enrolledCourses.length} {data.enrolledCourses.length === 1 ? 'programme' : 'programmes'} on file
            <span className="mx-1.5 text-rule">/</span>
            {completedLessons} of {totalLessons} lessons complete
            <span className="mx-1.5 text-rule">/</span>
            {remaining} to go
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/courses" className="lms-btn-secondary">
            <BookOpen className="h-4 w-4" strokeWidth={1.7} /> Browse programmes
          </Link>
          <Link to={`/learn/${activeCourse.courseId}`} className="lms-btn-primary">
            <Play className="h-4 w-4" fill="currentColor" strokeWidth={0} /> Resume learning
          </Link>
        </div>
      </div>

      <AnnouncementsCard prominent />

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0 space-y-6">
          <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-800 via-blue-700 to-sky-500 p-6 text-white shadow-2xl shadow-blue-900/15 sm:p-8">
            <div className="lms-hero-grid absolute inset-0" aria-hidden="true" />
            <div className="lms-hero-glow absolute inset-0" aria-hidden="true" />
            <div className="relative min-w-0">
              <div className="flex flex-wrap items-center gap-2.5">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-2.5 py-1 font-mono text-[8.5px] font-medium uppercase tracking-[0.13em] text-blue-50">
                  <span className="h-1.5 w-1.5 rounded-full bg-sky-300" /> Active programme
                </span>
                <span className="font-mono text-[8.5px] uppercase tracking-[0.13em] text-blue-100/70">
                  {courseRef(activeCourse.courseId)}
                </span>
              </div>
              <h2 className="mt-4 max-w-[26ch] text-[clamp(1.55rem,2.5vw,2.15rem)] font-semibold leading-[1.12] tracking-[-0.03em]">
                {activeCourse.courseName}
              </h2>
              <div className="mt-3 flex items-start gap-2 text-[12.5px] leading-5 text-blue-100">
                <Play className="mt-0.5 h-3.5 w-3.5 flex-none" fill="currentColor" strokeWidth={0} />
                <span>
                  <span className="text-blue-100/70">Next lesson · </span>
                  {activeCourse.nextClass.topic}
                </span>
              </div>

              <LearningPath
                completed={activeCourse.completedClasses}
                total={activeCourse.totalClasses}
                tone="dark"
                className="mt-7 max-w-[590px]"
              />

              <div className="mt-6 flex flex-wrap items-center gap-2.5">
                <Link
                  to={`/learn/${activeCourse.courseId}`}
                  className="inline-flex min-h-[38px] items-center justify-center gap-2 rounded-lg bg-white px-4 py-2.5 text-[12.5px] font-semibold text-blue-700 shadow-sm transition-colors hover:bg-blue-50"
                >
                  {activeCourse.progress >= 100 ? 'Open classroom' : 'Continue lesson'}
                  <ArrowRight className="h-4 w-4" strokeWidth={1.8} />
                </Link>
                <Link
                  to={`/course/${activeCourse.courseId}`}
                  className="inline-flex min-h-[38px] items-center justify-center gap-2 rounded-lg border border-white/25 px-4 py-2.5 text-[12.5px] font-semibold text-white transition-colors hover:bg-white/10"
                >
                  Programme details
                </Link>
              </div>
            </div>
          </section>

          <section>
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <h2 className="text-[18px] font-semibold tracking-[-0.02em] text-ink">Your programmes</h2>
                <p className="mt-1 text-[12.5px] text-ink-2">Pick up where you left off.</p>
              </div>
              <Link to="/courses" className="inline-flex items-center gap-1 text-[12px] font-semibold text-blue-700 hover:text-blue-800">
                All programmes <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.8} />
              </Link>
            </div>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              {data.enrolledCourses.map((course) => {
                const complete = course.progress >= 100;
                return (
                  <Link
                    key={course.courseId}
                    to={`/learn/${course.courseId}`}
                    className="group relative overflow-hidden rounded-2xl border border-rule bg-white p-4 shadow-[0_10px_30px_-28px_rgba(15,40,90,0.35)] transition-all hover:-translate-y-0.5 hover:border-blue-200 hover:shadow-xl hover:shadow-blue-900/5"
                  >
                    <div className="flex gap-4">
                      <div className="relative flex h-[86px] w-[92px] flex-none items-center justify-center overflow-hidden rounded-xl bg-gradient-to-br from-blue-100 to-sky-100">
                        <BookOpen className="absolute h-7 w-7 text-blue-300" strokeWidth={1.3} />
                        {course.thumbnail && (
                          <img
                            src={course.thumbnail}
                            alt=""
                            loading="lazy"
                            className="relative h-full w-full object-cover"
                            onError={(event) => { event.currentTarget.style.display = 'none'; }}
                          />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <Stamp tone={complete ? 'cleared' : 'issued'}>{complete ? 'Complete' : 'In progress'}</Stamp>
                          <ArrowUpRight className="h-4 w-4 flex-none text-slate-300 transition-colors group-hover:text-blue-600" strokeWidth={1.7} />
                        </div>
                        <h3 className="mt-2 line-clamp-2 text-[13.5px] font-semibold leading-5 text-ink group-hover:text-blue-800">
                          {course.courseName}
                        </h3>
                        <p className="mt-1 line-clamp-1 text-[11.5px] text-ink-2">{course.nextClass.topic}</p>
                        <p className="mt-3 font-mono text-[8.5px] uppercase tracking-[0.1em] text-ink-2">
                          {course.completedClasses} of {course.totalClasses} lessons complete
                        </p>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        </div>

        <aside className="space-y-5">
          <Panel>
            <PanelHead
              title="At a glance"
              action={<span className="font-mono text-[9px] uppercase tracking-[0.11em] text-ink-2">On file</span>}
            />
            <div className="grid grid-cols-3 divide-x divide-rule px-1 py-4 sm:px-2">
              {[
                { label: 'Programmes', value: data.enrolledCourses.length, icon: BookOpen },
                { label: 'Certificates', value: data.achievements.certificates, icon: Award },
                { label: 'Lessons', value: data.achievements.totalHours, icon: CheckCircle2 },
              ].map((item) => (
                <div key={item.label} className="min-w-0 px-1.5 text-center sm:px-3">
                  <item.icon className="mx-auto h-4 w-4 text-blue-500" strokeWidth={1.7} />
                  <p className="tnum mt-2.5 text-[23px] font-semibold leading-none tracking-[-0.04em] text-ink">{item.value}</p>
                  <p
                    title={item.label}
                    className="mt-1.5 truncate font-mono text-[7.5px] uppercase leading-3 tracking-[0.08em] text-ink-2 sm:text-[8px] sm:tracking-[0.11em]"
                  >
                    {item.label}
                  </p>
                </div>
              ))}
            </div>
          </Panel>

          {fees.length > 0 && (
            <Panel>
              <PanelHead title="Fees & access" />
              <div className="divide-y divide-rule">
                {fees.map((payment) => {
                  const balance = Math.max(payment.totalAmount - payment.amountPaid, 0);
                  return (
                    <div key={payment.courseId} className="px-5 py-3.5">
                      <p className="truncate text-[12.5px] font-semibold text-ink">{payment.courseName}</p>
                      <div className="mt-1.5 flex items-center justify-between gap-3">
                        <span className="tnum font-mono text-[9.5px] text-ink-2">
                          ₦{payment.amountPaid.toLocaleString('en-NG')} / ₦{payment.totalAmount.toLocaleString('en-NG')}
                        </span>
                        <Stamp tone={balance === 0 ? 'cleared' : 'pending'}>{balance === 0 ? 'Paid' : 'Balance'}</Stamp>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Panel>
          )}

          <Panel>
            <PanelHead title="Student desk" />
            <div className="space-y-0.5 p-2">
              <a href="mailto:info@lsem.org.ng" className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-[12.5px] text-ink transition-colors hover:bg-blue-50">
                <Mail className="h-4 w-4 flex-none text-blue-600" strokeWidth={1.6} />
                <span className="flex-1">Email the school</span>
                <ArrowUpRight className="h-3.5 w-3.5 text-ink-2" strokeWidth={1.7} />
              </a>
              <a href="tel:+2349058681595" className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-[12.5px] text-ink transition-colors hover:bg-blue-50">
                <Phone className="h-4 w-4 flex-none text-blue-600" strokeWidth={1.6} />
                <span className="flex-1">+234 905 868 1595</span>
                <ArrowUpRight className="h-3.5 w-3.5 text-ink-2" strokeWidth={1.7} />
              </a>
              <a href="https://wa.me/2349058681595" target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-[12.5px] text-ink transition-colors hover:bg-blue-50">
                <MessageCircle className="h-4 w-4 flex-none text-blue-600" strokeWidth={1.6} />
                <span className="flex-1">WhatsApp support</span>
                <ArrowUpRight className="h-3.5 w-3.5 text-ink-2" strokeWidth={1.7} />
              </a>
            </div>
          </Panel>
        </aside>
      </div>

      {data.recommendedCourses.length > 0 && (
        <section className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_300px]">
          <div className="min-w-0">
            <div>
              <h2 className="text-[18px] font-semibold tracking-[-0.02em] text-ink">Recommended for you</h2>
              <p className="mt-1 text-[12.5px] text-ink-2">More pathways from the LSEM register.</p>
            </div>
            <div className="mt-4 space-y-3">
              {data.recommendedCourses.map((course) => (
                <Link
                  key={course.id}
                  to={`/course/${course.id}`}
                  className="group flex overflow-hidden rounded-2xl border border-rule bg-white shadow-[0_10px_30px_-28px_rgba(15,40,90,0.35)] transition-all hover:-translate-y-0.5 hover:border-blue-200 hover:shadow-xl hover:shadow-blue-900/5"
                >
                  <div className="relative hidden w-[184px] flex-none overflow-hidden bg-gradient-to-br from-blue-600 to-sky-400 sm:block">
                    <BookOpen className="absolute left-4 top-4 h-6 w-6 text-white/60" strokeWidth={1.4} />
                    {course.image && (
                      <img
                        src={course.image}
                        alt=""
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        onError={(event) => { event.currentTarget.style.display = 'none'; }}
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-blue-950/55 via-transparent to-transparent" />
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col p-4">
                    <div className="flex items-center justify-between gap-3">
                      <span className="min-w-0 truncate font-mono text-[8.5px] uppercase tracking-[0.12em] text-ink-2">{courseRef(course.id)}</span>
                      <span className="tnum flex-none font-mono text-[10px] font-medium text-ink">{course.price}</span>
                    </div>
                    <h3 className="mt-2 line-clamp-2 text-[14px] font-semibold leading-snug text-ink group-hover:text-blue-800">
                      {course.title}
                    </h3>
                    <p className="mt-1.5 line-clamp-2 text-[11.5px] leading-5 text-ink-2">{course.description}</p>
                    <div className="mt-auto flex items-center justify-between border-t border-rule pt-3">
                      <span className="text-[11.5px] font-semibold text-blue-700">View programme</span>
                      <ArrowRight className="h-4 w-4 text-blue-500 transition-transform group-hover:translate-x-0.5" strokeWidth={1.8} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
          <aside className="relative flex min-h-[280px] flex-col overflow-hidden rounded-2xl bg-gradient-to-br from-blue-800 via-blue-700 to-sky-500 p-6 text-white shadow-lg shadow-blue-900/10">
            <div className="lms-hero-grid absolute inset-0" aria-hidden="true" />
            <div aria-hidden="true" className="absolute -right-10 -top-12 h-36 w-36 rounded-full border border-white/15" />
            <div className="relative flex h-full flex-col">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/20">
                <Sparkles className="h-5 w-5" strokeWidth={1.5} />
              </span>
              <p className="mt-5 font-mono text-[8.5px] uppercase tracking-[0.14em] text-blue-200">Programme register</p>
              <h3 className="mt-2 max-w-[18ch] text-[22px] font-semibold leading-[1.15] tracking-[-0.03em]">
                Not sure what to study next?
              </h3>
              <p className="mt-2.5 text-[12px] leading-5 text-blue-100">
                Compare every LSEM pathway, or ask admissions to map one to your export goal.
              </p>
              <Link
                to="/courses"
                className="mt-5 inline-flex min-h-[38px] items-center justify-center gap-2 rounded-lg bg-white px-4 py-2.5 text-[12px] font-semibold text-blue-700 shadow-sm transition-colors hover:bg-blue-50"
              >
                Browse all programmes <ArrowRight className="h-4 w-4" strokeWidth={1.8} />
              </Link>
              <p className="mt-auto pt-5 font-mono text-[8px] uppercase tracking-[0.12em] text-blue-100/60">
                No application fee · self-paced
              </p>
            </div>
          </aside>
        </section>
      )}
    </div>
  );
};

export default Dashboard;
